import type { ReactNode } from 'react'
import type { SpinChangePoint, SpinScopeStatus } from '../../../Repository/Visualization/SpinMeta.js'
import { spinEmptyStateText, type SpinStatusScope } from '../../../Model/Spin/spinStatus.js'
import { formatDate, parseDate } from '../../../Util/DateHandling.js'
import { GroupModes, type GroupModeDTO, type GroupModeKey } from '../../../Generated/GroupMode.ts'
import { parseColumnPicks, writeColumnPicks } from './SampleColumnWindow.js'
import { binSharingCell, binSpeciesCell, binsCell, processIdCell, spinIdCell } from './OtuTableCells.js'

// Pure, view-agnostic logic backing OtuDataTableView. Extracted from the component so the
// URL (de)serialisation, filter/sort math and column definitions can be unit-tested without a
// store, router or fetch. The component keeps only React state wiring; it delegates here.

// Filter operators the OTU table understands. Kept here (rather than in the header component) so
// both the table view and its header can share it without an import cycle.
export type FilterType = 'LIKE' | 'LESS_THAN' | 'GREATER_THAN' | 'LESS_THAN_OR_EQUAL' | 'GREATER_THAN_OR_EQUAL' | 'EQUAL'

export type Filter = { parameter: string; value: unknown; filterType: FilterType }

export type Column = {
  field: string
  label: string
  isNumber: boolean
  filterable: boolean
  sortable: boolean
  autocompleteName?: string
  /** Renders a cell from its value; [row] is the whole row, for cells that depend on more than their field. */
  transformer?: (value: unknown, row?: { evidenceMeasuredLater?: boolean }) => ReactNode
  formatter?: (value: unknown) => string
  /** Present on the reads columns — the sample-bucket's sample ids to sum reads over. */
  readsSampleIds?: string[]
  /** Render the header label rotated top-to-bottom (for long sample-name reads columns). */
  verticalHeader?: boolean
  /** The header's text when it differs from `label`, which then stays its title. */
  headerLabel?: string
  /** Hover text on the header. */
  title?: string
  /** SPIN evidence as of the "Evidence as of" date rather than a property of the SPIN itself. */
  pointInTime?: boolean
}

export type OtuTableMode = 'CLASSIC' | 'SPIN'

export type SortState = { sortField: string; sortDirection: 'ASC' | 'DESC' }

/** Symbol shown for each filter operator in the active-filter chips. */
export const FilterOp: Record<FilterType, string> = {
  LIKE: 'contains',
  LESS_THAN: '<',
  GREATER_THAN: '>',
  LESS_THAN_OR_EQUAL: '≤',
  GREATER_THAN_OR_EQUAL: '≥',
  EQUAL: '='
}

/** An active filter as its chip reads: the column's header label, the operator and the value. */
export const filterChipText = (filter: Filter, columns: readonly Column[]): string =>
  `${columns.find((c) => c.field === filter.parameter)?.label ?? filter.parameter} ${FilterOp[filter.filterType]} ${String(filter.value)}`

export const localeFormatter = (value: unknown): string => (value !== undefined && value !== null ? value.toLocaleString() : '-')

export const fixedFormatter = (value: unknown): string => (value ? (value as number).toFixed(2) : '-')

export function cnumberLocale(field: string, label: string, filterable?: boolean, sortable?: boolean): Column {
  return {
    field,
    label,
    filterable: filterable ?? true,
    sortable: sortable ?? true,
    isNumber: true,
    formatter: localeFormatter
  }
}

export function cnumberFixed(field: string, label: string, filterable?: boolean, sortable?: boolean): Column {
  return {
    field,
    label,
    filterable: filterable ?? true,
    sortable: sortable ?? true,
    isNumber: true,
    formatter: fixedFormatter
  }
}

export function cstring(field: string, label: string, filterable?: boolean, sortable?: boolean): Column {
  return {
    field,
    label,
    filterable: filterable ?? true,
    sortable: sortable ?? true,
    isNumber: false
  }
}

export const EVIDENCE_AS_OF_FIELD = 'evidenceEvaluatedAt'

const POINT_IN_TIME_TIP =
  'Point-in-time blast evidence: the value as of the date in the "Evidence as of" column, against the reference databases of that run. It changes with reference-database releases even when the SPIN does not.'

/**
 * Sources with no reference hit of their own: SPIN takes their hit, grade and process id from NCBI's evidence
 * (`DatasetSql.gradeBlastType`), and their OTUs have no process id or BIN to group by.
 */
const NCBI_EVIDENCE_SOURCES: ReadonlySet<string> = new Set(['RDP', 'Consensus'])

/** An evidence cell whose value was measured after the explicit As of, so the table hides it. */
export const MEASURED_LATER = 'measured later'

const NCBI_EVIDENCE_TIP = 'SPINs carry no evidence of their own for this blast type: the value is from the NCBI evidence.'

const LADDER: ReadonlyArray<readonly [field: string, label: string]> = [
  ['domain', 'Domain'],
  ['kingdom', 'Kingdom'],
  ['phylum', 'Phylum'],
  ['className', 'Class'],
  ['order', 'Order'],
  ['family', 'Family'],
  ['genus', 'Genus'],
  ['species', 'Species']
]

/**
 * The OTU table's columns in [mode], in display order, and only those the mode fills. Under RDP and Consensus SPIN
 * shows NCBI's evidence in the hit, grade and process id columns, so their headers say so.
 */
export function otuTableColumns(mode: OtuTableMode, blastType?: string): Column[] {
  const processId: Column = { field: 'processId', label: 'Process ID', filterable: false, sortable: false, isNumber: false, transformer: processIdCell }
  const binSharing: Column = { field: 'binSharing', label: 'BIN Sharing', filterable: false, sortable: false, isNumber: false, transformer: binSharingCell }
  const bins: Column = { field: 'bins', label: 'BINs', filterable: false, sortable: false, isNumber: false, transformer: binsCell }
  const binSpecies: Column = { field: 'binSpecies', label: 'BIN Species', filterable: false, sortable: false, isNumber: false, transformer: binSpeciesCell }
  const representation = cnumberLocale('representation', '% Samples', false, false)

  if (mode === 'CLASSIC') {
    return [
      cnumberLocale('clusterSize', 'Sum Cluster Size'),
      cnumberLocale('minClusterSize', 'Min Cluster Size'),
      cnumberFixed('avgClusterSize', 'Avg Cluster Size'),
      cnumberLocale('maxClusterSize', 'Max Cluster Size'),
      cnumberLocale('totalRawReads', 'Total Raw Reads'),
      processId,
      cnumberFixed('minHitPercentage', 'Min Hit %'),
      cnumberFixed('hitPercentage', 'Avg Hit %'),
      cnumberFixed('maxHitPercentage', 'Max Hit %'),
      cnumberFixed('minGradePercentage', 'Min Grade %'),
      cnumberFixed('gradePercentage', 'Avg Grade %'),
      cnumberFixed('maxGradePercentage', 'Max Grade %'),
      ...LADDER.map(([field, label]) => ctaxon(field, label)),
      binSharing,
      bins,
      binSpecies,
      representation
    ]
  }

  const ncbi = blastType !== undefined && NCBI_EVIDENCE_SOURCES.has(blastType)
  const evidence = (column: Column, fromGradeSource: boolean): Column => {
    const asNcbi = ncbi && fromGradeSource
    const render = column.transformer ?? ((value: unknown) => (column.formatter ? column.formatter(value) : (value as ReactNode)) || '-')
    return {
      ...column,
      transformer: (value, row) =>
        row?.evidenceMeasuredLater && (value === null || value === undefined) ? MEASURED_LATER : render(value, row),
      pointInTime: true,
      label: asNcbi ? `${column.label} (NCBI)` : column.label,
      title: asNcbi ? `${NCBI_EVIDENCE_TIP} ${POINT_IN_TIME_TIP}` : POINT_IN_TIME_TIP
    }
  }
  return [
    { field: 'otuId', label: 'SPIN', filterable: false, sortable: false, isNumber: false, transformer: spinIdCell },
    cnumberLocale('memberCount', 'Members'),
    cnumberLocale('clusterSize', 'Sum Cluster Size'),
    cnumberLocale('totalRawReads', 'Total Raw Reads'),
    ncbi ? { ...processId, label: 'Process ID (NCBI)', title: NCBI_EVIDENCE_TIP } : processId,
    ncbi ? { ...cnumberFixed('hitPercentage', 'Avg Hit % (NCBI)'), title: NCBI_EVIDENCE_TIP } : cnumberFixed('hitPercentage', 'Avg Hit %'),
    evidence(cnumberFixed('gradePercentage', 'Avg Grade %'), true),
    ...LADDER.map(([field, label]) => ctaxon(field, label)),
    evidence(binSharing, false),
    evidence(bins, false),
    binSpecies,
    representation,
    {
      field: EVIDENCE_AS_OF_FIELD,
      label: 'Evidence as of',
      title: ncbi ? `${NCBI_EVIDENCE_TIP} ${POINT_IN_TIME_TIP}` : POINT_IN_TIME_TIP,
      filterable: false,
      sortable: false,
      isNumber: false,
      transformer: (value, row) => {
        if (typeof value !== 'string' || value === '') return '—'
        const date = formatDate(parseDate(value))
        return row?.evidenceMeasuredLater ? `${date} (${MEASURED_LATER})` : date
      }
    }
  ]
}

export type InactiveFilter = { filter: Filter; text: string; onlyIn: string }

const MODE_LABEL: Record<OtuTableMode, string> = { CLASSIC: 'Classic (OTU)', SPIN: 'SPIN' }

/**
 * Splits [filters] into those [mode] applies and those on a column only the other mode has. The latter are kept, so
 * switching back restores them, but not applied: they would reject every row here. A filter on a column neither mode
 * knows stays active and keeps no rows, so its chip explains the empty table.
 */
export function splitFiltersByMode(
  filters: readonly Filter[],
  mode: OtuTableMode,
  blastType?: string
): { active: Filter[]; inactive: InactiveFilter[] } {
  const own = new Set(otuTableColumns(mode, blastType).map((c) => c.field))
  const otherMode: OtuTableMode = mode === 'CLASSIC' ? 'SPIN' : 'CLASSIC'
  const otherColumns = otuTableColumns(otherMode, blastType).filter((c) => !own.has(c.field))
  const active: Filter[] = []
  const inactive: InactiveFilter[] = []
  for (const filter of filters) {
    if (otherColumns.some((c) => c.field === filter.parameter)) {
      inactive.push({ filter, text: filterChipText(filter, otherColumns), onlyIn: MODE_LABEL[otherMode] })
    } else {
      active.push(filter)
    }
  }
  return { active, inactive }
}

const hasData = (column: Column, row: Record<string, unknown>): boolean => {
  if (column.field === 'representation' && row.representationPending) return true
  if (column.pointInTime && row.evidenceMeasuredLater) return true
  const value = row[column.field]
  if (column.autocompleteName) return displayTaxon(value) !== null
  return value !== null && value !== undefined && value !== ''
}

/**
 * The fields the table hides by itself because none of [rows] has a value in them; [rows] are all the filtered rows,
 * so the choice does not change from page to page. Read only until every column has shown data, and never stored,
 * so a column empty under one blast source or mode comes back under the next.
 */
export function autoHiddenColumns(columns: readonly Column[], rows: Iterable<object>): ReadonlySet<string> {
  const empty = new Set(columns.filter((c) => !c.readsSampleIds))
  let anyRow = false
  for (const r of rows) {
    anyRow = true
    for (const column of empty) if (hasData(column, r as Record<string, unknown>)) empty.delete(column)
    if (empty.size === 0) break
  }
  return anyRow ? new Set([...empty].map((c) => c.field)) : new Set()
}

/** A column shows unless the user hid it, or it is auto-hidden and the user never chose to show it. */
export const isColumnShown = (field: string, userHidden: Readonly<Record<string, boolean>>, autoHidden: ReadonlySet<string>): boolean =>
  userHidden[field] === undefined ? !autoHidden.has(field) : !userHidden[field]

/** The user's choices after toggling [field] from how it shows now. */
export const withColumnToggled = (
  field: string,
  userHidden: Readonly<Record<string, boolean>>,
  autoHidden: ReadonlySet<string>
): Record<string, boolean> => ({ ...userHidden, [field]: isColumnShown(field, userHidden, autoHidden) })

/** A stored taxon worth showing: the pipeline writes a missing rank as NULL, '' or 'nan'. */
export const displayTaxon = (value: unknown): string | null => {
  const v = typeof value === 'string' ? value.trim() : ''
  return v !== '' && v.toLowerCase() !== 'nan' ? v : null
}

/** A rank column: autocompleted, and empty where the pipeline wrote no taxon. */
export const ctaxon = (field: string, label: string): Column => ({
  ...cstringauto(field, label),
  transformer: (value) => displayTaxon(value) ?? ''
})

export function cstringauto(field: string, label: string, filterable?: boolean, sortable?: boolean): Column {
  return {
    field,
    label,
    filterable: filterable ?? true,
    sortable: sortable ?? true,
    isNumber: false,
    autocompleteName: field
  }
}

/** Parse a URL param that holds a `encodeURIComponent(JSON.stringify(...))` blob, falling back on any error. */
export function parseJsonParam<T>(params: URLSearchParams, key: string, fallback: T, label: string): T {
  const raw = params.get(key)
  if (!raw) return fallback
  try {
    return JSON.parse(decodeURIComponent(raw)) as T
  } catch (e) {
    console.error(`Error parsing ${label} from URL`, e)
    return fallback
  }
}

export const parseFiltersFromUrl = (params: URLSearchParams): Filter[] => parseJsonParam<Filter[]>(params, 'filters', [], 'filters')

export const parseFilterValuesFromUrl = (params: URLSearchParams): Record<string, string> =>
  parseJsonParam<Record<string, string>>(params, 'filterValues', {}, 'filter values')

export const parseHiddenColumnsFromUrl = (params: URLSearchParams): Record<string, boolean> =>
  parseJsonParam<Record<string, boolean>>(params, 'hiddenColumns', {}, 'hidden columns')

export function parseReadsGroupingFromUrl<T>(params: URLSearchParams): T | null {
  return parseJsonParam<T | null>(params, 'readsGrouping', null, 'reads grouping')
}

/** The read columns' share of the table's URL state; `readsSamples` are the picked columns past the column limit. */
export type ReadsViewState<G> = { showReads: boolean; readsGrouping: G | null; relativeReads: boolean; readsSamples?: string[] | null }

export const parseReadsViewFromUrl = <G>(params: URLSearchParams): ReadsViewState<G> => ({
  showReads: params.get('showReads') === '1',
  readsGrouping: parseReadsGroupingFromUrl<G>(params),
  relativeReads: params.get('relativeReads') === '1',
  readsSamples: parseColumnPicks(params.get('readsSamples'))
})

/** Writes [state] to [params] while the read columns are on. */
export function writeReadsViewToUrl<G>(params: URLSearchParams, state: ReadsViewState<G>): void {
  if (!state.showReads) return
  params.set('showReads', '1')
  if (state.readsGrouping) params.set('readsGrouping', encodeURIComponent(JSON.stringify(state.readsGrouping)))
  if (state.relativeReads) params.set('relativeReads', '1')
  if (state.readsSamples) params.set('readsSamples', writeColumnPicks(state.readsSamples))
}

/**
 * Operator for a taxonomy filter: a picked autocomplete suggestion is a stored value and matches exactly (which the
 * backend answers from per-rank indexes); typed text stays a substring match.
 */
export const taxonFilterType = (fromSuggestion: boolean): FilterType => (fromSuggestion ? 'EQUAL' : 'LIKE')

/** Spread columns hidden until the user shows them; an explicit `hiddenColumns` entry in the URL wins. */
export const DEFAULT_HIDDEN_COLUMNS: Readonly<Record<string, boolean>> = {
  minClusterSize: true,
  avgClusterSize: true,
  maxClusterSize: true,
  minHitPercentage: true,
  maxHitPercentage: true,
  minGradePercentage: true,
  maxGradePercentage: true
}

export const initialHiddenColumns = (params: URLSearchParams): Record<string, boolean> => ({
  ...DEFAULT_HIDDEN_COLUMNS,
  ...parseHiddenColumnsFromUrl(params)
})

/** Result of applying a filter: an empty/blank value removes the field's filter, otherwise it is upserted. */
export function withFilterApplied(filters: Filter[], field: string, value: string, filterType: FilterType): Filter[] {
  const withoutField = filters.filter((f) => f.parameter !== field)
  if (!value || value.trim() === '') return withoutField
  return [...withoutField, { parameter: field, value, filterType }]
}

export const withFilterCleared = (filters: Filter[], field: string): Filter[] => filters.filter((f) => f.parameter !== field)

export const hasFilter = (filters: Filter[], field: string): boolean => filters.some((f) => f.parameter === field)

/** The grouping selector's options: one row per OTU first, then the coarser groupings in the backend's order. */
export const groupModeOptions = (): GroupModeDTO[] => [GroupModes.NONE, ...Object.values(GroupModes).filter((mode) => mode.key !== 'NONE')]

/** Groupings keyed by a reference hit's process id or BIN, which RDP and Consensus OTUs do not have. */
const HIT_KEYED_GROUP_MODES: ReadonlySet<GroupModeKey> = new Set(['PROCESS_ID', 'BIN'])

/** Why [mode] cannot group the OTUs of [blastType], or null when it can. */
export function groupModeUnavailableReason(mode: GroupModeKey, blastType: string): string | null {
  if (!HIT_KEYED_GROUP_MODES.has(mode) || !NCBI_EVIDENCE_SOURCES.has(blastType)) {
    return null
  }
  return `${blastType} OTUs carry no ${GroupModes[mode].label}`
}

/**
 * The grouping the table applies for the user's [requested] one: OTUs while [blastType] cannot group by it, with the
 * toast that says so. [requested] itself stays, so a source that can group by it again does.
 */
export function effectiveGroupMode(requested: GroupModeKey, blastType: string): { groupMode: GroupModeKey; message: string | null } {
  const reason = groupModeUnavailableReason(requested, blastType)
  if (reason === null) {
    return { groupMode: requested, message: null }
  }
  return { groupMode: 'NONE', message: `${reason}; showing one row per OTU` }
}

/**
 * What an empty table says: that [grouping] keeps none of the rows when [groupedOut], else that the blast type has
 * no data.
 */
export const emptyTableText = (groupedOut: boolean, grouping: GroupModeKey, blastType: string): string =>
  groupedOut
    ? `No OTU has a ${GroupModes[grouping].label} under ${blastType}; group by OTUs or another rank.`
    : 'No analysis data found for the selected blast type.'

/** The sort a table without an explicit one shows, in both modes. */
export const DEFAULT_SORT_FIELD = 'totalRawReads'

/**
 * The sort the table applies and its header shows: [sortField] when one of [columns] sorts by it, else total reads
 * descending, which is also what both engines fall back to.
 */
export const effectiveSort = (columns: readonly Column[], sortField: string, sortDirection: 'ASC' | 'DESC'): SortState =>
  columns.some((c) => c.field === sortField && c.sortable)
    ? { sortField, sortDirection }
    : { sortField: DEFAULT_SORT_FIELD, sortDirection: 'DESC' }

/** Clicking a column header: toggle direction when it's the active sort, else sort that field descending. */
export function nextSort(current: SortState, field: string): SortState {
  if (current.sortField === field) {
    return { sortField: field, sortDirection: current.sortDirection === 'ASC' ? 'DESC' : 'ASC' }
  }
  return { sortField: field, sortDirection: 'DESC' }
}

/**
 * The source to show instead of [selected] once the scope's [sources] are known and lack it, as when a blast type was
 * picked before they arrived, with the toast that says so; null to keep [selected].
 */
export function blastTypeFallback<S extends string>(selected: S, sources: readonly S[] | null): { blastType: S; message: string } | null {
  if (!sources || sources.length === 0 || sources.includes(selected)) return null
  const blastType = sources[0]!
  return { blastType, message: `Blast type ${selected} is not available for this scope; showing ${blastType}` }
}

/** What a cut cube's table and export say about the OTUs they cover. */
export const truncationNote = ({ shown, total, unit = 'OTUs' }: { shown: number; total: number | null; unit?: 'OTUs' | 'SPINs' }): string =>
  (total === null
    ? `Showing the ${shown.toLocaleString()} most abundant ${unit}.`
    : `Showing the ${shown.toLocaleString()} most abundant ${unit} of ${total.toLocaleString()}.`) +
  (unit === 'SPINs' ? ' Filters and counts cover these SPINs only.' : ' Groups, filters and counts cover these OTUs only.')

/** Label for an entry in the SPIN "As of" change-point picker. */
export const formatAsOf = (cp: SpinChangePoint): string => `${formatDate(parseDate(cp.assignedAt))} · ${cp.changedOtus} changed`

/**
 * Why the CLASSIC/SPIN switch is not usable, or `null` when it is. A user who may see SPIN at all
 * (INSIGHT on the scope) keeps the switch visible even for scopes with no SPIN coverage — disabled with an
 * explanation beats silently hiding it, since "no SPIN yet" and "not entitled" looked identical.
 *
 * `algorithms` is `null` while the scope's SPIN state is still in flight and `[]`
 * once it answered "nothing here" — the two need different wording.
 */
/**
 * Whether a SPIN view (from a link, say) has to fall back to classic: the user may not use SPIN on this scope, or the
 * scope has no SPIN data. Without SPIN access there is no View select to leave SPIN by. Undecided while either is
 * still loading.
 */
export const spinViewUnavailable = ({
  accessKnown,
  spinAccess,
  algorithms
}: {
  accessKnown: boolean
  spinAccess: boolean
  algorithms: string[] | null
}): boolean => accessKnown && (!spinAccess || (algorithms !== null && algorithms.length === 0))

export const spinModeDisabledReason = (algorithms: string[] | null, scope: SpinStatusScope, status?: SpinScopeStatus): string | null => {
  if (algorithms === null) return 'Checking whether SPIN data exists for this scope…'
  if (algorithms.length === 0) return spinEmptyStateText(status, scope)
  return null
}
